import { InfoCircleOutlined } from "@ant-design/icons";
import { Collapse, Typography } from "antd";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useState } from "react";
import { Trans } from "react-i18next";

import { FilterContext } from "./types/filterEditor";

const HELP_KEY = "ui.offer-filter.help";
const VALUE_HANDLING_KEYS = ["is_not_empty", "not_equals_null", "is_true_false", "example"];
const COLLAPSED_STORAGE_KEY = "filter-help-collapsed";
const PANEL_KEY = "help";

/** Storage can be unavailable (private mode, blocked site data) — the help then just starts open. */
const readCollapsed = (): boolean => {
    try {
        return localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true";
    } catch {
        return false;
    }
};

const writeCollapsed = (collapsed: boolean) => {
    try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
    } catch {
        // not persisted, the toggle still works for this page
    }
};

interface FilterHelpProps {
    context: FilterContext;
}

/** How the filter combines conditions and how the BE operators treat empty, null and boolean-like values. */
export const FilterHelp = ({ context }: FilterHelpProps) => {
    const { t } = usePaldockTranslation(HELP_KEY);
    const [collapsed, setCollapsed] = useState(readCollapsed);

    const rich = (key: string) => <Trans i18nKey={`${HELP_KEY}.${key}`} components={{ b: <strong /> }} />;

    const handleChange = (activeKeys: string[]) => {
        const nextCollapsed = !activeKeys.includes(PANEL_KEY);
        setCollapsed(nextCollapsed);
        writeCollapsed(nextCollapsed);
    };

    return (
        <Collapse
            className="mt-4"
            size="small"
            activeKey={collapsed ? [] : [PANEL_KEY]}
            onChange={handleChange}
            items={[
                {
                    key: PANEL_KEY,
                    label: (
                        <span className="flex items-center gap-2">
                            <InfoCircleOutlined />
                            {t("title")}
                        </span>
                    ),
                    children: (
                        // global sub-collapsible-overrides.css zeroes `.ant-collapse-content-box` padding
                        <div className="flex flex-col gap-2 px-3 py-2">
                            <div>
                                <div>{t(context === "cascade" ? "intro_cascade" : "intro_offer")}</div>
                                <div>{rich("and")}</div>
                                <div>{rich("or")}</div>
                            </div>
                            <div>
                                <Typography.Text strong>{t("value_handling")}</Typography.Text>
                                <ul className="list-disc pl-5 m-0">
                                    {VALUE_HANDLING_KEYS.map((key) => (
                                        <li key={key}>{rich(key)}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ),
                },
            ]}
        />
    );
};
